import { scoreColour as conditionScoreColour } from "@/lib/conditions/score-colour";
import type { AreaSport } from "@/lib/planner/graph-types";
import type { SkiConvention } from "@/lib/maps/mountain-atlas";

// European ski colours and PeakWave's normalised MTB grades. North American
// ski source grades use a different mapping below; MTB ingestion already maps
// its source scale, so applying the ski shift to bike trails would be incorrect.
export const DIFFICULTY_COLOUR: Record<string, string> = {
  novice: "#7BB662",
  easy: "#3B82C4",
  intermediate: "#C0392B",
  advanced: "#1B2840",
  expert: "#0A0A0A",
  freeride: "#8B5CF6",
};

export const UNKNOWN_DIFFICULTY_COLOUR = "#9CA3AF";

export function difficultyColour(
  difficulty: string | null,
  sport: AreaSport = "ski",
  convention: SkiConvention = "europe",
): string {
  difficulty = difficulty?.trim().toLowerCase() ?? null;
  if (!difficulty) return UNKNOWN_DIFFICULTY_COLOUR;
  if (sport === "ski" && convention === "north-america") {
    if (difficulty === "easy") return DIFFICULTY_COLOUR.novice;
    if (difficulty === "intermediate") return DIFFICULTY_COLOUR.easy;
  }
  if (sport === "ski" && convention === "japan" && difficulty === "easy") return DIFFICULTY_COLOUR.novice;
  return DIFFICULTY_COLOUR[difficulty] ?? UNKNOWN_DIFFICULTY_COLOUR;
}

export function difficultyLabel(
  difficulty: string | null,
  sport: AreaSport = "ski",
  convention: SkiConvention = "europe",
): string {
  difficulty = difficulty?.trim().toLowerCase() ?? null;
  if (sport === "ski" && convention === "coronet-peak") {
    if (difficulty === "novice") return "Green circle";
    if (difficulty === "easy") return "Blue square";
    if (difficulty === "intermediate") return "Red square";
    if (difficulty === "advanced" || difficulty === "expert") return "Black diamond";
  }
  if (sport === "ski" && convention === "north-america") {
    if (difficulty === "easy" || difficulty === "novice") return "Green circle";
    if (difficulty === "intermediate") return "Blue square";
    if (difficulty === "advanced") return "Black diamond";
    if (difficulty === "expert") return "Double black diamond";
  }
  if (sport === "ski" && convention === "japan" && difficulty === "easy") return "Green";
  if (sport === "mtb") {
    switch (difficulty) {
      case "novice":
        return "Green";
      case "easy":
        return "Blue";
      case "intermediate":
        return "Red";
      case "advanced":
        return "Black";
      case "expert":
        return "Double black";
      case "freeride":
        return "Pro line";
      default:
        return "Unrated";
    }
  }
  switch (difficulty) {
    case "novice":
      return "Green";
    case "easy":
      return "Blue";
    case "intermediate":
      return "Red";
    case "advanced":
      return "Black";
    case "expert":
      return "Double black";
    case "freeride":
      return "Freeride";
    default:
      return "Unrated";
  }
}

// Symbols supplement colour for MTB and North American ski runs.
export function difficultySymbol(
  difficulty: string | null,
  sport: AreaSport = "ski",
  convention: SkiConvention = "europe",
): string {
  difficulty = difficulty?.trim().toLowerCase() ?? null;
  if (sport === "ski" && convention === "coronet-peak") {
    if (difficulty === "novice") return "●";
    if (difficulty === "easy" || difficulty === "intermediate") return "■";
    if (difficulty === "advanced" || difficulty === "expert") return "◆";
  }
  if (sport === "ski" && convention === "north-america") {
    if (difficulty === "novice" || difficulty === "easy") return "●";
    if (difficulty === "intermediate") return "■";
    if (difficulty === "advanced") return "◆";
    if (difficulty === "expert") return "◆◆";
  }
  if (sport !== "mtb") return "";
  switch (difficulty) {
    case "novice":
      return "●"; // ● filled circle (green)
    case "easy":
      return "■"; // ■ filled square (blue)
    case "intermediate":
      return "◆"; // ◆ diamond (red)
    case "advanced":
      return "◆"; // ◆ diamond (black)
    case "expert":
      return "◆◆"; // ◆◆ double diamond
    case "freeride":
      return "◆◆"; // ◆◆ double diamond (pro line)
    default:
      return "";
  }
}

/**
 * Score lens colour for a Closed run: a dark slate that is on no score ramp and much darker
 * than the light greys used for limited data and missing scores, so a closure never reads as
 * a poor score or as missing data. The TrailSheet "Closed" badge and the lens key use it too.
 */
export const CLOSED_RUN_COLOUR = "#374151";

// Score lens colour: reuse the platform's score ramp so the map matches the
// badges elsewhere. Degraded trails (no trustworthy aspect) are muted. Closed
// runs take the closed colour whatever their score.
export function scoreColour(score: number | null, degraded: boolean, closed = false): string {
  if (closed) return CLOSED_RUN_COLOUR;
  if (score == null) return UNKNOWN_DIFFICULTY_COLOUR;
  if (degraded) return "#B8B8B8";
  return conditionScoreColour(score);
}

export const ASPECT_LABEL: Record<string, string> = {
  N: "North-facing",
  NE: "Northeast-facing",
  E: "East-facing",
  SE: "Southeast-facing",
  S: "South-facing",
  SW: "Southwest-facing",
  W: "West-facing",
  NW: "Northwest-facing",
};
